import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

const STEPS = [
  {
    step: '01',
    title: 'Enroll',
    text: 'Create your account in a few minutes.',
  },
  {
    step: '02',
    title: 'Choose a plan',
    text: '1, 2, or 3 months of full access.',
  },
  {
    step: '03',
    title: 'Study with the tutor',
    text: 'Lessons, questions, and progress in one place.',
  },
  {
    step: '04',
    title: 'Practice the exam',
    text: 'Quizzes and mock exams before test day.',
  },
];

function StoryVisual({ index }) {
  if (index === 0) {
    return (
      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#c5a046]">New account</p>
        <div className="mt-4 space-y-3">
          {['Full name', 'Country', 'City'].map((label) => (
            <div key={label} className="rounded-xl border border-[#e4e0d6] px-4 py-3 text-sm text-[#7c8590]">
              {label}
            </div>
          ))}
          <div className="rounded-full bg-[#1b365d] px-4 py-3 text-center text-sm font-semibold text-white">
            Create account
          </div>
        </div>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ['1 month', false],
          ['2 months', true],
          ['3 months', false],
        ].map(([label, featured]) => (
          <div
            key={label}
            className={`rounded-2xl p-4 ${featured ? 'bg-[#1b365d] text-white' : 'bg-white text-[#0f2137]'}`}
          >
            <p className="text-xs uppercase tracking-[0.12em] text-[#c5a046]">{featured ? 'Popular' : 'Plan'}</p>
            <p className="mt-3 text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {label}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (index === 2) {
    return (
      <div className="flex min-h-[240px] flex-col justify-end gap-3 rounded-3xl bg-[#0f2137] p-6">
        <p className="max-w-[220px] self-end rounded-2xl rounded-br-md bg-white/10 px-4 py-3 text-sm text-white">
          I keep missing graph questions.
        </p>
        <p className="max-w-[240px] rounded-2xl rounded-bl-md bg-[#c5a046] px-4 py-3 text-sm text-[#0f2137]">
          Start with the axis labels. I&apos;ll mark the next one with you.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white p-6 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#c5a046]">Practice exam</p>
      <p className="mt-2 text-4xl font-semibold text-[#1b365d]" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Ready
      </p>
      <div className="mt-5 space-y-3">
        {[
          ['Math', '80%'],
          ['Science', '74%'],
          ['Language Arts', '69%'],
          ['Social Studies', '77%'],
        ].map(([name, width]) => (
          <div key={name}>
            <div className="mb-1 text-xs text-[#5b6570]">{name}</div>
            <div className="h-2 overflow-hidden rounded-full bg-[#e8eef6]">
              <div className="h-full rounded-full bg-[#005f61]" style={{ width }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepList({ active, onSelect }) {
  return (
    <ol className="space-y-3">
      {STEPS.map((step, index) => {
        const isActive = index === active;
        return (
          <li key={step.step}>
            <button
              type="button"
              onClick={() => onSelect?.(index)}
              className={`w-full cursor-pointer rounded-2xl border-0 px-4 py-4 text-left transition-colors ${
                isActive ? 'bg-[#1b365d] text-white' : 'bg-transparent text-[#0f2137]'
              }`}
            >
              <span className={`text-xs font-semibold tracking-[0.14em] ${isActive ? 'text-[#e8d59a]' : 'text-[#c5a046]'}`}>
                {step.step}
              </span>
              <span className="mt-1 block text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {step.title}
              </span>
              <span className={`mt-1 block text-sm ${isActive ? 'text-white/80' : 'text-[#5b6570]'}`}>{step.text}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function StickyStory() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 960px)').matches
  );
  const [active, setActive] = useState(0);
  const pinned = wide && !reduce;

  useEffect(() => {
    const media = window.matchMedia('(min-width: 960px)');
    const update = () => setWide(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!pinned) return undefined;
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      const passed = Math.min(Math.max(-el.getBoundingClientRect().top, 0), Math.max(total, 0));
      const progress = total > 0 ? passed / total : 0;
      const next = Math.min(STEPS.length - 1, Math.floor(progress * STEPS.length));
      setActive((current) => (current === next ? current : next));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pinned]);

  if (!pinned) {
    return (
      <section className="bg-[#f3f6fb] px-5 py-16" aria-label="How preparation works">
        <div className="mx-auto grid max-w-[1180px] gap-6">
          {STEPS.map((step, index) => (
            <article key={step.step} className="grid gap-4 rounded-3xl bg-white p-5 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold tracking-[0.14em] text-[#c5a046]">{step.step}</p>
                <h3 className="mt-1 text-2xl text-[#0f2137]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="mt-2 text-[#5b6570]">{step.text}</p>
              </div>
              <StoryVisual index={index} />
            </article>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="relative h-[320vh] bg-[#f3f6fb]" aria-label="How preparation works">
      <div className="sticky top-24 mx-auto grid min-h-[calc(100vh-8rem)] max-w-[1180px] items-center gap-10 px-5 py-10 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#c5a046]">How it works</p>
          <h2
            className="mt-2 mb-6 text-4xl text-[#0f2137]"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}
          >
            Four steps. Then you study.
          </h2>
          <StepList active={active} onSelect={setActive} />
        </div>
        <div className="min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
            >
              <StoryVisual index={active} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
